import React, { useState, useRef } from 'react';
import { UserIcon, CameraIcon, TrashIcon } from '@heroicons/react/24/outline';
import './ProfilePictureUpload.css';

const ProfilePictureUpload = ({
  currentImage,
  onImageChange,
  onImageRemove,
  disabled = false,
  size = 'large', // 'small', 'medium', 'large'
  showRequirementsInOverlay = false // Show file requirements in hover overlay
}) => {
  const [dragActive, setDragActive] = useState(false);
  const [preview, setPreview] = useState(currentImage);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);



  // File validation
  const validateFile = (file) => {
    const maxSize = 5 * 1024 * 1024; // 5MB
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];

    if (!allowedTypes.includes(file.type)) {
      return 'Please upload a valid image file (JPEG, PNG, or WebP)';
    }

    if (file.size > maxSize) {
      return 'Image size must be less than 5MB';
    }

    return null;
  };

  // Handle file selection
  const handleFileSelect = (file) => {
    setError('');
    
    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreview(e.target.result);
    };
    reader.readAsDataURL(file);

    // Call parent callback
    if (onImageChange) {
      onImageChange(file);
    }
  };

  // Handle drag events
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  // Handle drop
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (disabled) return;

    const files = e.dataTransfer.files;
    if (files && files[0]) {
      handleFileSelect(files[0]);
    }
  };

  // Handle file input change
  const handleInputChange = (e) => {
    const files = e.target.files;
    if (files && files[0]) {
      handleFileSelect(files[0]);
    }
  };

  // Handle click to upload
  const handleUploadClick = () => {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Handle remove image
  const handleRemove = (e) => {
    e.stopPropagation();
    setPreview(null);
    setError('');
    
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    if (onImageRemove) {
      onImageRemove();
    }
  };

  // Get size classes
  const getSizeClasses = () => {
    switch (size) {
      case 'small':
        return 'profile-picture-upload-small';
      case 'medium':
        return 'profile-picture-upload-medium';
      case 'large':
      default:
        return 'profile-picture-upload-large';
    }
  };

  return (
    <div className={`profile-picture-upload ${getSizeClasses()}`}>
      <div
        className={`upload-container ${dragActive ? 'drag-active' : ''} ${disabled ? 'disabled' : ''}`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={handleUploadClick}
      >
        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/jpg,image/png,image/webp"
          onChange={handleInputChange}
          disabled={disabled}
          className="file-input"
        />

        {/* Image preview or placeholder */}
        <div className="image-container">
          {preview ? (
            <>
              <img
                src={preview}
                alt="Profile"
                className="profile-image"
              />
              <div className="image-overlay">
                <div className="overlay-content">
                  <div className="change-photo-section">
                    <CameraIcon className="overlay-icon" />
                    <span className="overlay-text">Change Photo</span>
                  </div>
                  {showRequirementsInOverlay && (
                    <div className="requirements-section">
                      <div className="requirements-title">File Requirements:</div>
                      <div className="requirements-list">
                        <div className="requirement-item">📁 JPEG, PNG, WebP</div>
                        <div className="requirement-item">📏 Maximum 5MB</div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              {!disabled && (
                <button
                  type="button"
                  className="remove-button"
                  onClick={handleRemove}
                  aria-label="Remove profile picture"
                >
                  <TrashIcon className="remove-icon" />
                </button>
              )}
            </>
          ) : (
            <div className="placeholder">
              <div className="placeholder-icon-container">
                <UserIcon className="placeholder-icon" />
              </div>
              <div className="placeholder-content">
                <CameraIcon className="upload-icon" />
                <span className="upload-text">
                  {size === 'small' ? 'Add Photo' : 'Upload Profile Picture'}
                </span>
                {size !== 'small' && (
                  <span className="upload-hint">
                    Drag & drop or click to browse
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="upload-error">
          {error}
        </div>
      )}

      {/* File requirements - only show if not in overlay */}
      {size !== 'small' && !showRequirementsInOverlay && (
        <div className="upload-requirements">
          <p>Supported formats: JPEG, PNG, WebP</p>
          <p>Maximum size: 5MB</p>
        </div>
      )}
    </div>
  );
};

export default ProfilePictureUpload;
